/**
 * What a failed HUMINT call means for the screen. A failed face check is mostly a *not checked*
 * verdict (never a match, never a clearance); a few change the page instead: not a member of the
 * case, the extension switched off, a photo refused (unreadable, too large, a format the service
 * cannot decode), or the operator's cancel. A failed save (an enrolment, a capture upload, a
 * link) reads the same way everywhere, each save adding the refusals it has its own words for.
 */
import type { NotCheckedReason } from '@/components/humint/types'
import { ApiError, UploadNetworkError, UploadTimeoutError } from '@/services/api/client'
import type { HumintErrorCode } from '@/services/api/humint'
import { ProbeDecodeError } from '../probe/prepare-probe'
import type { ProbeRefusal } from '../probe/probe-file'
import type { ProbeNotice } from './verdict'

/**
 * Why the capture screen shows a notice instead of a check: the photo was refused before it
 * was sent (`ProbeRefusal`) or by the server (`ProbeNotice`), or checks are off.
 */
export type CaptureNotice = ProbeRefusal | ProbeNotice | 'unavailable'

export type CheckFailure =
  | { kind: 'cancelled' }
  | { kind: 'notice'; notice: CaptureNotice }
  | { kind: 'not_member' }
  | { kind: 'extension_off' }
  | { kind: 'not_checked'; reason: NotCheckedReason }

const HUMINT_ERROR_CODES: readonly HumintErrorCode[] = [
  'extension_disabled',
  'extension_unavailable',
  'audit_unavailable',
]

/** Statuses of a gateway or proxy that could not reach the API or the faces service. */
const UNREACHABLE_STATUSES = new Set([502, 503, 504])

/** HTTP refusals of the photo itself: an ingress body cap, or a format the API refuses. */
const PROBE_REFUSAL_STATUS: Readonly<Record<number, ProbeNotice>> = {
  413: 'too_large',
  415: 'unsupported',
}

/** The `code` of the API's `{detail: {code, message}}` refusal; undefined without one. */
export function detailCode(error: ApiError): unknown {
  const detail = (error.data as { detail?: unknown } | null | undefined)?.detail
  return detail && typeof detail === 'object' && 'code' in detail ? detail.code : undefined
}

/** The server's own sentence of a refusal (`detail.message`, or a plain `detail`); '' for anything else. */
export function detailMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return ''
  const detail = (error.data as { detail?: unknown } | null | undefined)?.detail
  if (typeof detail === 'string') return detail
  const message = (detail as { message?: unknown } | null | undefined)?.message
  return typeof message === 'string' ? message : ''
}

/** The operator's cancel (or an unmount) aborted the request. */
export function isAbort(error: unknown): boolean {
  return (error as { name?: unknown } | null)?.name === 'AbortError'
}

/** No HTTP answer: the XHR transport says so, fetch rejects with a TypeError. */
export function isNoAnswer(error: unknown): boolean {
  return error instanceof UploadNetworkError || error instanceof TypeError
}

/** The `detail.code` of a HUMINT 503, or null for any other error. */
export function humintErrorCode(error: unknown): HumintErrorCode | null {
  if (!(error instanceof ApiError) || error.status !== 503) return null
  const code = detailCode(error)
  return HUMINT_ERROR_CODES.find(known => known === code) ?? null
}

/** The extension gate refused (off, or not available on this deployment right now). */
export function isExtensionOff(error: unknown): boolean {
  const code = humintErrorCode(error)
  return code === 'extension_disabled' || code === 'extension_unavailable'
}

/** 403: not a member of the case (SC-036); a superadmin is let in instead (ADR-0051). */
export function isNotMember(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403
}

const notChecked = (reason: NotCheckedReason): CheckFailure => ({ kind: 'not_checked', reason })

/** An API answer that is no verdict: the gate, membership, the audit, or the photo refused. */
function classifyApiError(error: ApiError): CheckFailure {
  if (isNotMember(error)) return { kind: 'not_member' }
  if (isExtensionOff(error)) return { kind: 'extension_off' }
  if (humintErrorCode(error) === 'audit_unavailable') return notChecked('audit_unavailable')
  const refusal = PROBE_REFUSAL_STATUS[error.status]
  if (refusal) return { kind: 'notice', notice: refusal }
  return notChecked(UNREACHABLE_STATUSES.has(error.status) ? 'service_unavailable' : 'error')
}

/**
 * Classify a failed check. `online` is `navigator.onLine`: a request that got no answer
 * while offline reads "no connection", otherwise "service unavailable".
 */
export function classifyCheckError(error: unknown, online: boolean): CheckFailure {
  if (isAbort(error)) return { kind: 'cancelled' }
  if (error instanceof ProbeDecodeError) return { kind: 'notice', notice: 'unreadable' }
  if (error instanceof UploadTimeoutError) return notChecked('timeout')
  if (error instanceof UploadNetworkError) {
    return notChecked(online ? 'service_unavailable' : 'offline')
  }
  if (error instanceof ApiError) return classifyApiError(error)
  return notChecked('error')
}

// ── A failed save: an enrolment, a capture upload, a link (slices 04–07) ─────────

/** What every save says of a failure, whatever refusals it has words for. */
export type SaveProblem =
  | 'offline'
  | 'timeout'
  | 'error'
  | 'audit_unavailable'
  | 'forbidden'
  | 'conflict'
  | 'rejected'
  | 'extension_off'

export type SaveFailure<P extends string> =
  | { kind: 'cancelled' }
  | { kind: 'problem'; problem: P | SaveProblem }

/** The refusals a save has its own words for. */
export interface SaveRefusals<P extends string> {
  /** By status, ahead of the shared reading (a link's 404: the subject is gone). */
  byStatus?: Readonly<Partial<Record<number, P>>>
  /** By the 422 `detail.code`; any other 422 is `rejected`. */
  byCode: Readonly<Record<string, P>>
}

/** A 4xx the same request gets again: not a timeout (408) or a rate limit (429). */
export const isRefusal = (status: number) =>
  status >= 400 && status < 500 && status !== 408 && status !== 429

/**
 * An answer that is no success: 403 `forbidden` is a viewer or a non-member (a superadmin is let
 * in, ADR-0051); 409 `conflict` an id already used with other content, which resending cannot
 * fix; a 503 the extension gate, the audit or any other outage; any other 4xx a request the
 * server will not take (say a name too long), which resending the frozen request cannot help.
 */
function saveProblemOf<P extends string>(
  error: ApiError,
  refusals: SaveRefusals<P>
): P | SaveProblem {
  const own = refusals.byStatus?.[error.status]
  if (own) return own
  switch (error.status) {
    case 403:
      return 'forbidden'
    case 409:
      return 'conflict'
    case 422: {
      const code = detailCode(error)
      return (
        Object.entries(refusals.byCode).find(([refusal]) => refusal === code)?.[1] ?? 'rejected'
      )
    }
    case 503:
      if (isExtensionOff(error)) return 'extension_off'
      return humintErrorCode(error) === 'audit_unavailable' ? 'audit_unavailable' : 'error'
    default:
      return isRefusal(error.status) ? 'rejected' : 'error'
  }
}

/**
 * Classify a failed save. `online` is `navigator.onLine`: a request that got no answer while
 * offline reads "no connection", otherwise "service unavailable". The client's own timeout
 * arrives as `UploadTimeoutError` from an upload and a JSON call alike.
 */
export function classifySaveError<P extends string>(
  error: unknown,
  online: boolean,
  refusals: SaveRefusals<P>
): SaveFailure<P> {
  const problem = (value: P | SaveProblem): SaveFailure<P> => ({ kind: 'problem', problem: value })
  if (error instanceof UploadTimeoutError) return problem('timeout')
  if (isAbort(error)) return { kind: 'cancelled' }
  if (error instanceof ApiError) return problem(saveProblemOf(error, refusals))
  if (isNoAnswer(error)) return problem(online ? 'error' : 'offline')
  return problem('error')
}
